import { AsyncPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MotionMasterMessage, UiConfig } from 'motion-master-client';
import { Observable } from 'rxjs';

import { MagnetDistanceUserData } from '@app/models/magnet-distance';
import { DeviceService } from '@app/services/device.service';
import { ModalService } from '@app/services/modal.service';
import { BrakeControlComponent } from '@app/shared/brake-control/brake-control.component';
import {
  EncoderCalibrationIteration,
  CirculoEncoderCalibrationPlotComponent,
} from '@app/shared/circulo-encoder-calibration-plot/circulo-encoder-calibration-plot.component';
import { CoggingTorqueCompensationComponent } from '@app/shared/cogging-torque-compensation/cogging-torque-compensation.component';
import { DeviceBaseComponent } from '@app/shared/device-base.component';
import { DeviceListDropdownComponent } from '@app/shared/device-list-dropdown/device-list-dropdown.component';
import { DeviceParameterFormComponent } from '@app/shared/device-parameter-form/device-parameter-form.component';
import { DistanceCircleComponent } from '@app/shared/distance-circle/distance-circle.component';
import { IsPdoControlModeDirective } from '@app/shared/is-pdo-control-mode.directive';
import { OffsetDetectionComponent } from '@app/shared/offset-detection/offset-detection.component';
import { PositionControllerTuningComponent } from '@app/shared/position-controller-tuning/position-controller-tuning.component';
import { QuickOpenButtonComponent } from '@app/shared/quick-open-button/quick-open-button.component';
import { ResetFaultButtonComponent } from '@app/shared/reset-fault-button/reset-fault-button.component';
import { SaveToDeviceButtonComponent } from '@app/shared/save-to-device-button/save-to-device-button.component';
import { SignalGeneratorComponent } from '@app/shared/signal-generator/signal-generator.component';
import { SystemIdentificationComponent } from '@app/shared/system-identification/system-identification.component';
import { TorqueControllerTuningComponent } from '@app/shared/torque-controller-tuning/torque-controller-tuning.component';
import { VelocityControllerTuningComponent } from '@app/shared/velocity-controller-tuning/velocity-controller-tuning.component';
import { DeviceParameter } from '@app/store/device-parameter.model';
import { Device } from '@app/store/device.model';
import { TuningController } from '@app/store/tuning-controller.model';
import { getCurrentValue } from '@app/util';

const ControllerType = MotionMasterMessage.Request.EnableMotionController.ControllerType;

@Component({
  selector: 'app-lab',
  templateUrl: './lab.component.html',
  imports: [
    RouterLink,
    DeviceListDropdownComponent,
    BrakeControlComponent,
    ResetFaultButtonComponent,
    SaveToDeviceButtonComponent,
    QuickOpenButtonComponent,
    CirculoEncoderCalibrationPlotComponent,
    DistanceCircleComponent,
    DeviceParameterFormComponent,
    CoggingTorqueCompensationComponent,
    SystemIdentificationComponent,
    OffsetDetectionComponent,
    PositionControllerTuningComponent,
    VelocityControllerTuningComponent,
    TorqueControllerTuningComponent,
    FormsModule,
    IsPdoControlModeDirective,
    SignalGeneratorComponent,
    AsyncPipe,
  ],
})
export class LabComponent extends DeviceBaseComponent implements OnInit {
  private deviceService = inject(DeviceService);
  private modalService = inject(ModalService);
  private router = inject(Router);

  devices$ = this.deviceService.allDevices$;

  positionTuningController$: Observable<TuningController>;
  velocityTuningController$: Observable<TuningController>;
  torqueTuningController$: Observable<TuningController>;

  showBrakeControlInNavbar$: Observable<boolean>;

  externalScaledMeasurementParameters$: Observable<DeviceParameter[]>;

  hasValidSensorConfiguration$: Observable<boolean>;

  tuningControllers$: Observable<TuningController[]>;

  uiConfig$: Observable<UiConfig>;

  motionControllerFilter: boolean;

  encoderCalibrationTesting = true;

  encoderCalibrationPlotData: EncoderCalibrationIteration[] = [
    { color: 'gray', name: 'Gray', series: [] },
    { color: 'blue', name: 'Blue', series: [] },
  ];

  distances: MagnetDistanceUserData[] = [
    { color: 'red', distance: 0.7, value: '0.20', position: 500, label: 'TOO CLOSE' },
    { color: 'green', distance: 0.8, value: '0.30', position: 100, label: 'ACCEPTABLE' },
    { color: 'blue', distance: 0.4, value: '0.40', position: 800, label: 'SUBOPTIMAL' },
  ];

  constructor() {
    super();

    for (let i = 0; i < 350; i++) {
      this.encoderCalibrationPlotData[0].series.push([i, Math.random() * 500 - 250]);
      this.encoderCalibrationPlotData[1].series.push([i, Math.random() * 500 - 250]);
    }

    this.encoderCalibrationPlotData[0].series[200][1] = -125;
    this.encoderCalibrationPlotData[1].series[200][1] = 60;
  }

  onDeviceChange(device: Device) {
    this.positionTuningController$ = this.deviceService.selectDeviceTuningControllerByType(device, 'position');
    this.velocityTuningController$ = this.deviceService.selectDeviceTuningControllerByType(device, 'velocity');
    this.torqueTuningController$ = this.deviceService.selectDeviceTuningControllerByType(device, 'torque');

    this.showBrakeControlInNavbar$ = this.deviceService.selectDeviceShowBrakeControlInNavbar(device);

    this.externalScaledMeasurementParameters$ = this.deviceService.selectDeviceParameters(device, 0x2038);

    this.hasValidSensorConfiguration$ = this.deviceService.selectDeviceHasValidSensorConfiguration(device);

    this.tuningControllers$ = this.deviceService.selectDeviceTuningControllers(device);

    this.uiConfig$ = this.deviceService.selectDeviceUiConfig(device);
  }

  ngOnInit() {
    this.device = getCurrentValue(this.deviceService.selectDeviceByPosition(0));
  }

  onSelectDevice(device: Device) {
    this.device = device;
  }

  onBackToDashboard() {
    this.router.navigateByUrl('/');
  }

  onStop() {
    this.deviceService.requestStopDevice(this.device);
  }

  onEnablePositionController() {
    this.deviceService.requestEnableMotionController(this.device, ControllerType.POSITION, this.motionControllerFilter);
  }

  onEnableVelocityController() {
    this.deviceService.requestEnableMotionController(this.device, ControllerType.VELOCITY, this.motionControllerFilter);
  }

  onEnableTorqueController() {
    this.deviceService.requestEnableMotionController(this.device, ControllerType.TORQUE, this.motionControllerFilter);
  }

  onDisableMotionController() {
    this.deviceService.requestDisableMotionController(this.device);
  }

  onFileBrowser() {
    this.modalService.openFileBrowserModal(this.device);
  }
}
